/* eslint-disable no-nested-ternary */
import React, { useState, useContext, useEffect } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import { ActionIcon, ConfirmationDialog } from '@mummssoftware/common-ui';
import { isAgencyEnabled } from '@mummssoftware/utils/utils';
import { PNCounterContext } from '@mummssoftware/global-context/PNCounter';
import { REFRESH_PN } from '@mummssoftware/global-context/PNCounter/constants';
import {
  OrdersGlobalContext,
  SET_DISPLAY_CONFIRMATION,
  SET_ORDER_VERIFIED,
  SET_CREATE_ORDER,
} from '@mummssoftware/global-context/OrdersGlobalContext';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import { CarePlanFrequency, CPF } from '@mummssoftware/utils/constants';
import {
  VFGlobalContext,
  CLEAR_EDITING_DATA,
  SET_ORDERS_MODE,
  GlobalContext,
  CLOSE_WIDGET,
  OPEN_WIDGET,
} from '@mummssoftware/global-context';
import { ADM } from '@mummssoftware/utils/orders';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import { isPSOType, isDisType, isOverrideOrder } from '../Statements/utils';
import { Context } from '../../context';
import PSOMenu from '../MenuContent/PSOMenu';
import CheckOrderMenu from '../MenuContent/OrderVerification/CheckOrderMenu';
import {
  GET_STANDING_ORDERS,
  SAVE_PSO,
  SET_PECOS_OPEN,
} from '../Context/constants';
import {
  SubmitPNDiv,
  SubmitTextDiv,
  SubmitButtonsDiv,
  PSOWrapper,
  LoadingMenuDiv,
} from '../../components/layout/WrapperDiv';
import { checkIsOrder } from '../../utils/utils';
import defaults from '../../translations/en.json';

type SubmitNoteProps = {
  fixed?: boolean;
  orderText?: string;
  hide?: boolean | null;
};

const SubmitNote = ({ fixed, orderText, hide }: SubmitNoteProps) => {
  const {
    orderCreated,
    resetWorkflow,
    orderDisabled,
    standingOrders,
    psoText,
    updatePnCount,
    dispatch,
    disOrderMap,
    isOverrideVerifOrderAgency,
    checkIfPECOSEligible,
    pecosOpen,
    isPhysicianRoleGroup,
  } = useContext(ProgressNotesContext);
  const {
    orderType,
    savedNote,
    orderVerified,
    dispatchOrders,
    cancelOrderWorkflow,
    noteText,
  } = useContext(OrdersGlobalContext);
  const {
    savedStandingOrders,
    saveNote,
    clearSavedNote,
    patient,
    agency,
    currentUser,
    saveStandingOrderv2,
    deleteNote,
    entityKey,
    saveNoteText,
    saveNoteTextInContext,
    siteId,
  } = useContext(Context);
  const { VFordersMode, dispatchVF } = useContext(VFGlobalContext);
  const { dispatch: dispatchGlobal } = useContext(GlobalContext);

  const pnContext = useContext(PNCounterContext);
  const { attemptedVisitNote } = useContext(GlobalContext);

  const isOrderType = !!orderType;
  const isOrderInProcess = isOrderType && !orderCreated;
  const canOrderBeCancelled = !orderVerified && !orderCreated;

  const isDischarge = orderType && isDisType(orderType) && disOrderMap;
  const isDischargeNote = isDischarge && !disOrderMap[orderType as string];

  const overrideVerification =
    isOverrideOrder(orderType) && isPhysicianRoleGroup;
  const isOverrideVerifyOrderAgency =
    isOverrideVerifOrderAgency || overrideVerification;
  // isOrderToBeVerified are all orders but note types
  const isOrderToBeVerified =
    isOrderType && !isDischargeNote && !isOverrideVerifyOrderAgency;
  const [orderVerificationStep, setVerificationStep] = useState<boolean>(
    isOrderToBeVerified,
  );
  // this is disabled once an order has been created
  const [discardDisabled, setDiscardDisabled] = useState<boolean>(false);
  const [psoDisabled, setPSODisabled] = useState<boolean>(false);
  const [psoStep, setPSOstep] = useState<boolean>(
    !savedStandingOrders && isPSOType(orderType),
  );

  const [pecoStep, setPecos] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<any>(null);

  const triggerSaveNote = async () => {
    setSaveDisabled(true);
    saveNoteTextInContext(true);
    updateNotesCount();
  };

  useEffect(() => {
    if (saveNoteText && !!noteText) {
      onSaveNote();
    }
  }, [saveNoteText, !!noteText]);

  const onSaveNote = async () => {
    // dispatch note text somewhere?
    noteText &&
      (await saveNote(noteText).then((res: any) => {
        if (
          (!(res instanceof Response) && res.patientprogressnoteentitydto) ||
          entityKey
        ) {
          // only update the count if saving the note succeeds and needed
          updateNotesCount();
        }
      }));

    // reset value even if save note fails
    resetWorkflow();
  };

  const updateNotesCount = () => {
    if (updatePnCount && Object.keys(updatePnCount)) {
      Object.entries(updatePnCount).forEach((payload) => {
        pnContext.dispatch &&
          pnContext.dispatch({
            type: REFRESH_PN,
            payload: payload[1],
          });
      });
    }
  };

  const onDiscardNote = async () => {
    setDiscardIconDisabled(true);
    /**
     * scenario 1 : if there is a tagged note to MED, CPP => API DELETE NOTE
     * scenario 2 : There is only a simple note => DISCARD TEXT
     */

    /**
     *  There are 2 functionalities of DISCARD NOTE
     * if a note has been tagged for MM, CPP (but not an ORDER) and exiting the screen before typing text => DELETE
     */
    const taggedNote = savedNote
      ? savedNote.patientprogressnoteentitydto
      : null;
    // is there an order attached
    const isOrder = checkIsOrder(savedNote);

    const orderPresent = taggedNote ? isOrder : null;

    // if a not has been tagged but NOT to an order
    if (taggedNote && !orderPresent) {
      await deleteNote(savedNote?.id as any, agency);
    }

    // reset value
    clearSavedNote();
    resetWorkflow();
  };

  const onDiscardOrder = () => {
    if (VFordersMode) {
      dispatchGlobal({
        type: CLOSE_WIDGET,
        payload: { componentName: CarePlanFrequency },
      });
      dispatchVF({ type: CLEAR_EDITING_DATA, payload: null });
      dispatchVF({ type: SET_ORDERS_MODE, payload: false });
    }
    // reset value
    clearSavedNote();
    resetWorkflow();
  };
  // todo
  const checkAdmitOrder = async (event: any) => {
    const ignorePECOS = isAgencyEnabled(
      window.env.ignorePECOSRequirement,
      currentUser,
    );
    if (ignorePECOS) {
      // proceed to normal verification step
      setAnchorEl('anchorElEventNotPersist');
    } else {
      const checkStatus = await checkIfPECOSEligible();
      // if PECOS_ELIGIBLE
      if (checkStatus.admitOk) {
        // proceed to normal verification step
        setAnchorEl('anchorElEventNotPersist');
      } else {
        // reset
        dispatchOrders({
          type: SET_CREATE_ORDER,
          payload: false,
        });
        setPecos(true);
        setAnchorEl('anchorElEventNotPersist');
      }
    }
  };

  useEffect(() => {
    if (pecosOpen) {
      setPecos(true);
      setAnchorEl('anchorElEventNotPersist');
      setDiscardDisabled(false);
    }
  }, [pecosOpen]);

  /**
   * onSaveOrder to be called from Done Icon button
   */
  const onSaveOrder = (event: any) => {
    if (psoStep) {
      // open the PSO dialog box
      setAnchorEl(event.currentTarget);

      dispatch({
        type: GET_STANDING_ORDERS,
        payload: true,
      });
    } else if (orderVerificationStep) {
      // todo modify not needed CPF check
      if (orderType === CPF) {
        dispatchVF({ type: SET_ORDERS_MODE, payload: true });
        dispatchGlobal({
          type: OPEN_WIDGET,
          payload: { componentName: CarePlanFrequency },
        });
      } else {
        setAnchorEl(event.currentTarget);
      }
    } else {
      setDiscardDisabled(true);

      // isDISNonOrderType
      //  DEATH || REVOKED || TRANSFERRED;
      dispatchOrders({
        type: SET_CREATE_ORDER,
        payload: true,
      });
      // @note from SET_CREATE_ORDER OrderStatement has the logic to save and refresh screen
    }
  };

  const savePSO = async () => {
    if (standingOrders) {
      const patientStandingOrder: mumms.PatientStandingOrderDTOv2 = {
        createdatetime: formatPostDate(new Date()),
        createpersonnumber: currentUser.personNumber as string,
        id: '',
        patientnumber: patient.patientNumber,
        standingordername: standingOrders.name,
        standingordershortname: standingOrders.shortname,
        standingorderdescription: standingOrders.description,
        siteid: siteId || patient.siteId.toString(),
        templatebasedtext: psoText,
      };
      const prnPatientStandingOrder = {
        ...patientStandingOrder,
      };

      const entityName = standingOrders.shortname;

      dispatch({
        type: SAVE_PSO,
        payload: true,
      });
      // reset get PSO from stand alone and Order PSO
      dispatch({
        type: GET_STANDING_ORDERS,
        payload: null,
      });

      await saveStandingOrderv2(
        patient.patientNumber,
        agency,
        prnPatientStandingOrder,
        entityName,
      );

      // NEW : create order right after PSO
      dispatchOrders({
        type: SET_CREATE_ORDER,
        payload: true,
      });
    }
  };

  /**
   * onSavePSO to be called from Dialog Box
   */
  const onSavePSO = async (event: any) => {
    if (isOverrideVerifyOrderAgency) {
      setAnchorEl(null);
      setPSOstep(false);
      setPSODisabled(true);
      savePSO();
    } else {
      setAnchorEl(event.currentTarget);
      setPSOstep(false);
    }
  };

  const getButtonText = () => {
    let text: string;
    if (isOrderInProcess) {
      if (isDischargeNote) {
        text = defaults.dischargeSubmit;
      } else if (orderText) {
        text = orderText.toLocaleUpperCase();
      } else {
        text = defaults.orderSubmit;
      }
    } else {
      text = defaults.noteSubmit;
    }
    return text;
  };

  const onCancelClick = () => {
    setAnchorEl(null);
    dispatchOrders({
      type: SET_ORDER_VERIFIED,
      payload: false,
    });
    cancelOrderWorkflow();
    // todo modify not needed CPF check
    if (orderType === CPF) {
      dispatchVF({
        type: SET_ORDERS_MODE,
        payload: false,
      });
      dispatchVF({
        type: CLEAR_EDITING_DATA,
        payload: null,
      });
      dispatchOrders({
        type: SET_DISPLAY_CONFIRMATION,
        payload: false,
      });
    }
  };

  const onCancelPECO = () => {
    setAnchorEl(null);
    setPecos(false);
    dispatch({
      type: SET_PECOS_OPEN,
      payload: false,
    });
  };

  /**
   * onOrderVerified : order read back and verified
   * 1. create order
   * 2. reset verification step
   * 3. reset order verified
   */
  const onOrderVerified = (event: any) => {
    setAnchorEl(null);
    setDiscardDisabled(true);

    setVerificationStep(false);
    dispatchOrders({
      type: SET_ORDER_VERIFIED,
      payload: false,
    });

    if (isPSOType(orderType) && standingOrders) {
      // save PSO if standingOrders
      savePSO();
    } else {
      dispatchOrders({
        type: SET_CREATE_ORDER,
        payload: true,
      });
    }
  };

  const getMenuContent = () => {
    if (pecoStep) {
      return <p>{defaults.admPecoError}</p>;
    } else if (psoStep) {
      if (standingOrders) {
        return <PSOMenu standingOrders={standingOrders} />;
      }
    } else if (orderVerificationStep) {
      return (
        <CheckOrderMenu
          onCancelClick={onCancelClick}
          onOrderVerified={onOrderVerified}
        />
      );
    }
    return <LoadingMenuDiv>{defaults.loading}</LoadingMenuDiv>;
  };

  // dialog box attributes
  const dialogTitle = pecoStep
    ? defaults.pecoEligible
    : psoStep
    ? defaults.gso
    : defaults.verifyOrder;
  const fullWidth = !!(psoStep && standingOrders);
  const [saveIconDisabled, setSaveDisabled] = useState<boolean>(false);

  const [discardIconDisabled, setDiscardIconDisabled] = useState<boolean>(
    false,
  );
  // ADMIT Order and verification process, open before verification
  const isADMNotOverride = orderType === ADM && !isOverrideVerifyOrderAgency;
  const onRecordOrder = isADMNotOverride ? checkAdmitOrder : onSaveOrder;

  const onSave = isOrderInProcess ? onRecordOrder : triggerSaveNote;
  const onDiscard = isOrderInProcess ? onDiscardOrder : onDiscardNote;
  const onSaveDisabled = isOrderInProcess ? orderDisabled : saveIconDisabled;

  const onDiscardDisabled = canOrderBeCancelled
    ? discardDisabled
    : discardIconDisabled;

  // todo color palette COMMON UI
  const htmlSaveColor = psoDisabled || onSaveDisabled ? 'grey' : 'black';
  const htmlDisableColor =
    psoDisabled || onDiscardDisabled || !!attemptedVisitNote ? 'grey' : 'black';

  const style = {
    margin: 0,
    width: 20,
    height: 20,
  };
  return (
    <SubmitPNDiv id="submit-div" fixed={fixed} hide={hide}>
      <SubmitTextDiv>{getButtonText()}</SubmitTextDiv>
      <SubmitButtonsDiv>
        <ActionIcon
          style={style}
          Icon={DoneIcon}
          onClick={onSave}
          htmlColor={htmlSaveColor}
          disabled={onSaveDisabled}
        />
        <ActionIcon
          style={style}
          Icon={CloseIcon}
          onClick={onDiscard}
          htmlColor={htmlDisableColor}
          disabled={onDiscardDisabled || !!attemptedVisitNote}
        />
      </SubmitButtonsDiv>
      <ConfirmationDialog
        title={dialogTitle}
        disableBackdropClick
        fullWidth={fullWidth}
        maxWidth="md"
        open={!!anchorEl}
        handleClick={pecoStep ? onCancelPECO : (onSavePSO as any)}
        noIcon={!fullWidth && !pecoStep}
        noScroll={window.innerHeight > 400}
      >
        <PSOWrapper id="pso-wrapper">{getMenuContent()}</PSOWrapper>
      </ConfirmationDialog>
    </SubmitPNDiv>
  );
};

export default SubmitNote;
